<template>
    <div class="my-orders px-0 pt-0 pt-sm-0 pb-0">

        <div v-if="orders.length > 0 || completedOrders.length > 0">
            <!-- page headline -->
            <v-row>
                <v-col cols="12" class="py-0">
                    <heading-section class="d-flex">
                        <v-icon class="mr-2">mdi-package-variant-closed</v-icon>
                        {{ $vuetify.lang.t('$vuetify.widgets.orders.title') }}
                    </heading-section>
                </v-col>
            </v-row>
            <!-- /page headline -->

            <!-- orders -->
            <template v-if="orders.length > 0">
                <v-card class="pa-4 mb-6" tile flat v-for="order in orders" :key="`order${order.id}`" v-if="order.order_items.length > 0">
                    <v-row>
                        <!-- shipping info -->
                        <v-col cols="12" sm="5" class="pt-0 pb-0 mb-8">
                            <h2>
                                {{ $vuetify.lang.t('$vuetify.widgets.orders.order_title', order.order_number, $options.filters.date(order.created_at)) }}
                            </h2>
                            <div class="body-1 font-weight-bold mt-2">
                                {{ $vuetify.lang.t('$vuetify.widgets.orders.address') }}:
                            </div>
                            <div class="body-1">
                        <span class="text--secondary">
                            {{ $vuetify.lang.t('$vuetify.widgets.orders.address_name') }}:
                        </span>
                                {{ order.delivery_address.delivery_address_firstname }}
                                {{ order.delivery_address.delivery_address_lastname }}
                            </div>
                            <div class="body-1" v-if="order.delivery_address.delivery_address_company">
                        <span class="text--secondary">
                            {{ $vuetify.lang.t('$vuetify.widgets.orders.address_company') }}:
                        </span>
                                {{ order.delivery_address.delivery_address_company }}
                            </div>
                            <div class="body-1">
                        <span class="text--secondary">
                            {{ $vuetify.lang.t('$vuetify.widgets.orders.address_street') }}:
                        </span>
                                {{ order.delivery_address.delivery_address_street }}
                                {{ order.delivery_address.delivery_address_number }}
                            </div>
                            <div class="body-1">
                        <span class="text--secondary">
                            {{ $vuetify.lang.t('$vuetify.widgets.orders.address_zip') }}:
                        </span>
                                {{ order.delivery_address.delivery_address_zip }}
                            </div>
                            <div class="body-1">
                        <span class="text--secondary">
                            {{ $vuetify.lang.t('$vuetify.widgets.orders.address_city') }}:
                        </span>
                                {{ order.delivery_address.delivery_address_city }}
                            </div>
                            <div class="body-1">
                        <span class="text--secondary">
                            {{ $vuetify.lang.t('$vuetify.widgets.orders.address_country') }}:
                        </span>
                                {{ order.delivery_address.delivery_address_country }}
                            </div>
                        </v-col>
                        <v-col cols="12" sm="7" class="pt-0 pb-0">
                            <div :class="{'mb-9': orderItemIndex !== order.order_items.length - 1 }" v-for="(orderItem, orderItemIndex) in order.order_items"
                                 :key="`order-item${orderItem.id}`">
                                <order-item :order-item="orderItem"></order-item>
                            </div>
                        </v-col>
                    </v-row>
                    <!-- /shipping info -->
                </v-card>
            </template>
            <!-- /orders -->

            <!-- completed orders -->
            <template v-if="completedOrders.length > 0">
                <v-row>
                    <v-col cols="12" class="py-0">
                        <heading-section class="d-flex">
                            {{ $vuetify.lang.t('$vuetify.widgets.orders.completed_title') }}
                        </heading-section>
                    </v-col>
                </v-row>

                <v-row id="order-filter"
                       class="px-7 px-lg-0 mt-3"
                       dense
                       align="center"
                       no-gutters>
                    <v-col cols="auto" class="flex-shrink-0 py-0">
                        <v-icon class="text-shadow--default">mdi-filter-outline</v-icon>
                    </v-col>
                    <v-col class="pl-2 pl-lg-3">
                        <item-filter type="select"
                                     @input="onChangeFilter('filterCompletedOrder', $event)"
                                     :items="years"
                                     item-text="name"
                                     item-value="id"
                                     :value="filterCompletedOrder"></item-filter>
                    </v-col>
                </v-row>

                <v-row>
                    <v-col class="d-flex" cols="12" sm="6" v-for="completedOrder in completedOrders" :key="`completed-order${completedOrder.id}`">
                        <v-card class="pa-4" tile flat>
                            <order-item expandable :order-item="completedOrder"></order-item>
                        </v-card>
                    </v-col>
                </v-row>
            </template>
            <!-- /completed orders -->
        </div>
        <div v-else class="mt-3">
            <v-card class="pa-2"
                    flat
                    tile>
                <h2 class="headline font-weight-bold mb-3">
                    {{ $vuetify.lang.t('$vuetify.widgets.orders.no_entry') }}
                </h2>
                <p class="body-1 mb-0">
                    {{ $vuetify.lang.t('$vuetify.widgets.orders.no_entry_desc') }}
                </p>
            </v-card>
        </div>
    </div>
</template>
<script>
    // 3rd
    import {
        mapActions,
        mapState
    } from 'vuex';
    // components
    import HeadingSection from '../components/HeadingSection';
    import ItemFilter from '../components/ItemFilter';
    import OrderItem from '../components/OrderItem';
    import PageNotFound from '../components/PageNotFound';
    // models
    import Order from '../models/Order';
    import CompletedOrder from '../models/CompletedOrder';
    // utils
    import { setPageTitle } from '../utils/helper';

    export default {
        name: 'my_orders',
        components: {
            HeadingSection,
            ItemFilter,
            OrderItem,
            PageNotFound,
        },
        computed: {
            ...mapState([
                'filterCompletedOrder',
            ]),
            orders() {
                return Order.query().orderBy('created_at', 'desc').get();
            },
            completedOrders() {
                return CompletedOrder.getByYear(this.filterCompletedOrder);
            },
            years() {
                const years = CompletedOrder.getAvailableYearsForFilter();
                const yearsObj = years.map((entry) => {
                    return {
                        id: entry,
                        name: this.$vuetify.lang.t('$vuetify.widgets.orders.filter', entry),
                    };
                });
                return [...yearsObj];
            },
        },
        methods: {
            ...mapActions([
                'setFilter',
            ]),
            onChangeFilter(i, v) {
                this.$vuetify.goTo('#order-filter', {
                    offset: document.querySelector('.ios-top-bar').offsetHeight,
                });
                this.setFilter({ item: i, value: v });
            },
        },
        mounted() {
            this.$nextTick(() => {
                const years = CompletedOrder.getAvailableYearsForFilter();
                if (years.length === 0) return;
                const yearsObj = years.map((entry) => {
                    return {
                        id: entry,
                        name: this.$vuetify.lang.t('$vuetify.widgets.orders.filter', entry),
                    };
                });
                this.setFilter({ item: 'filterCompletedOrder', value: yearsObj[0].id });
            });
        }
    }
</script>
